import React, { useState } from 'react';
import { FileUpload } from './FileUpload';
import { MediaGallery } from './MediaGallery';
import { MediaViewer } from './MediaViewer';
import { MediaAsset, MediaUploadOptions } from '../../types/media';
import { SectionHeader } from '../ui';

interface MediaManagerProps {
  stakeholderId?: string;
  organizationId?: string;
  allowUpload?: boolean;
  allowDelete?: boolean;
  viewMode?: 'grid' | 'list';
  className?: string;
}

export const MediaManager: React.FC<MediaManagerProps> = ({
  stakeholderId,
  organizationId,
  allowUpload = true,
  allowDelete = false,
  viewMode = 'grid',
  className = '',
}) => {
  const [selectedMedia, setSelectedMedia] = useState<MediaAsset | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const uploadOptions: MediaUploadOptions = {
    stakeholderId,
    organizationId,
  };

  const handleUploadComplete = (mediaAsset: MediaAsset) => {
    // Refresh the gallery to show the new upload
    setRefreshKey(prev => prev + 1);
  };

  const handleUploadError = (error: string) => {
    console.error('Upload error:', error);
    // You could show a toast notification here
  };

  const handleMediaSelect = (media: MediaAsset) => {
    setSelectedMedia(media);
    setShowViewer(true);
  };

  const handleMediaDelete = (media: MediaAsset) => {
    // Refresh the gallery after deletion
    setRefreshKey(prev => prev + 1);
    if (selectedMedia?.id === media.id) {
      setSelectedMedia(null);
      setShowViewer(false);
    }
  };

  const handleCloseViewer = () => {
    setShowViewer(false);
    setSelectedMedia(null);
  };

  return (
    <div className={`media-manager font-ui ${className}`}>
      <div className="space-y-6">
        {allowUpload && (
          <section>
            <SectionHeader title="Upload" />
            <FileUpload
              onUploadComplete={handleUploadComplete}
              onUploadError={handleUploadError}
              options={uploadOptions}
              multiple={true}
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
            />
          </section>
        )}

        <section>
          <SectionHeader title="Files" />
          <MediaGallery
            key={refreshKey}
            stakeholderId={stakeholderId}
            organizationId={organizationId}
            onMediaSelect={handleMediaSelect}
            onMediaDelete={handleMediaDelete}
            allowDelete={allowDelete}
            viewMode={viewMode}
          />
        </section>
      </div>

      {/* Full-screen viewer */}
      {showViewer && selectedMedia && (
        <MediaViewer
          media={selectedMedia}
          onClose={handleCloseViewer}
          onDelete={handleMediaDelete}
          allowDelete={allowDelete}
        />
      )}
    </div>
  );
};
